// src/components/events/edit/EventEditBinding.jsx

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import EventEditDescription from "./EventEditDescription";

export default function EventEditBinding({ event = {}, onSubmit }) {
  const navigate = useNavigate();

  // event.heroImagePath ※Firestore에 저장된 “이미지 URL” (혹은 빈 문자열)
  const {
    heroImagePath = "",
    title: initTitle = "",
    subtitle: initSubtitle = "",
    startDate: initStartDate = "",
    endDate: initEndDate = "",
    content: initContent = "",
  } = event;

  const [heroFile, setHeroFile]       = useState(null);
  const [heroPreview, setHeroPreview] = useState("");
  const [title, setTitle]             = useState(initTitle);
  const [subtitle, setSubtitle]       = useState(initSubtitle);
  const [content, setContent]         = useState(initContent);
  const [saving, setSaving]           = useState(false);

  // 날짜 포맷 변환 헬퍼
  const toHyphen = s => s.replace(/\./g, "-");
  const toDot    = s => s.replace(/-/g, ".");

  const [startInput, setStartInput] = useState(
    initStartDate ? toHyphen(initStartDate) : ""
  );
  const [endInput, setEndInput] = useState(
    initEndDate ? toHyphen(initEndDate) : ""
  );
  const [startDate, setStartDate] = useState(initStartDate);
  const [endDate, setEndDate] = useState(initEndDate);

  const textareaRef = useRef(null);

  // subtitle textarea 높이 자동 확장
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [subtitle]);

  // hero preview 처리
  useEffect(() => {
    if (heroFile) {
      // 새로 선택된 File 이라면 FileReader로 preview 설정
      const reader = new FileReader();
      reader.onload = e => setHeroPreview(e.target.result);
      reader.readAsDataURL(heroFile);
    } else if (heroImagePath) {
      // Firestore로부터 받은 URL(HTTP)이 있다면 그대로 preview
      setHeroPreview(heroImagePath);
    } else {
      setHeroPreview("");
    }
  }, [heroFile, heroImagePath]);

  // props가 바뀌면 내부 state 동기화
  useEffect(() => {
    setTitle(initTitle);
    setSubtitle(initSubtitle);
    setContent(initContent);
    setStartInput(initStartDate ? toHyphen(initStartDate) : "");
    setEndInput(initEndDate ? toHyphen(initEndDate) : "");
    setStartDate(initStartDate);
    setEndDate(initEndDate);
  }, [initTitle, initSubtitle, initStartDate, initEndDate, initContent]);

  // 상태값·D-Day 계산
  const now = new Date();
  const sd = startDate
    ? new Date(startDate.replace(/\./g, "-") + "T00:00:00")
    : now;
  const ed = endDate
    ? new Date(endDate.replace(/\./g, "-") + "T00:00:00")
    : now;

  let statusLabel = "진행예정",
    statusColor = "bg-purple-600";
  if (now >= sd && now <= ed) {
    statusLabel = "진행중";
    statusColor = "bg-green-500";
  } else if (now > ed) {
    statusLabel = "종료";
    statusColor = "bg-gray-600";
  }
  const daysLeft = Math.ceil((ed - now) / (1000 * 60 * 60 * 24));

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    await onSubmit({
      title,
      subtitle,
      startDate,
      endDate,
      content,
      heroFile,        // 새로 올린 File (또는 null)
    });
    setSaving(false);
  };

  const handleCancel = () => {
    navigate('/admin', { state: { tab: 'events' } });
  };

  return (
    <div className="bg-[#121212] text-white min-h-screen">
      
      {/* ─── 배너(히어로) 영역 ─── */}
      <div
        className="relative h-[450px] bg-center bg-cover"
        style={{ backgroundImage: `url(${heroPreview})` }}
      >
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 flex h-full flex-col justify-center max-w-7xl mx-auto px-6 space-y-4">
          {/* ─── 상단 버튼 영역 ─── */}
          <div className="flex justify-between w-full">
            <div>
              <input
                id="heroUpload"
                type="file"
                accept="image/*"
                className="hidden"
                disabled={saving}
                onChange={e => setHeroFile(e.target.files?.[0] || null)}
              />
              <label
                htmlFor="heroUpload"
                className={`bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded cursor-pointer ${
                  saving ? 'opacity-50 pointer-events-none' : ''
                }`}
              >
                배너 이미지 (2560 x 440)
              </label>
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-500 disabled:opacity-50"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className={`px-4 py-2 rounded text-white ${
                  saving ? 'bg-gray-600' : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {saving ? '저장중...' : event.id ? '저장하기' : '등록하기'}
              </button>
            </div>
          </div>

          {/* ─── 상태·D-Day, 날짜 입력 ─── */}
          <div className="flex items-center space-x-4 text-sm">
            <span className={`px-2 py-1 rounded text-sm font-medium ${statusColor}`}>
              {statusLabel}
            </span>
            {statusLabel === "진행중" && daysLeft > 0 && daysLeft <= 4 && (
              <span className="px-2 py-1 rounded text-sm font-medium bg-red-500">
                D-{daysLeft}
              </span>
            )}
            <div className="flex items-center space-x-2">
              <input
                type="date"
                value={startInput}
                onChange={e => {
                  setStartInput(e.target.value);
                  setStartDate(toDot(e.target.value));
                }}
                className="bg-neutral-400/30 border-b border-gray-300 focus:outline-none"
                disabled={saving}
              />
              <span>~</span>
              <input
                type="date"
                value={endInput}
                onChange={e => {
                  setEndInput(e.target.value);
                  setEndDate(toDot(e.target.value));
                }}
                className="bg-neutral-400/30 border-b border-gray-300 focus:outline-none"
                disabled={saving}
              />
              <span>(UTC+9)</span>
            </div>
          </div>

          {/* ─── 제목 입력 ─── */}
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="이벤트 제목을 입력하세요"
            className="w-4/5 bg-transparent text-5xl font-extrabold leading-snug border-b border-gray-300 focus:outline-none"
            disabled={saving}
          />

          {/* ─── 간략 설명 textarea ─── */}
          <textarea
            ref={textareaRef}
            value={subtitle}
            onChange={e => setSubtitle(e.target.value)}
            placeholder="간략 설명을 입력하세요"
            className="w-1/2 bg-neutral-800 px-3 py-2 rounded text-lg text-gray-200 focus:outline-none resize-none overflow-hidden"
            disabled={saving}
          />
        </div>
      </div>

      {/* ─── 탭 네비 ─── */}
      <div className="bg-[#1f1f1f] border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-6">
          <ul className="flex justify-center space-x-8 text-sm">
            <li className="border-b-2 border-white py-4 text-white font-medium">이벤트 정보</li>
          </ul>
        </div>
      </div>

      {/* ─── 본문 에디터 ─── */}
      <div className="container mx-auto px-6 py-8 pb-12 max-w-7xl">
        <EventEditDescription content={content} onChange={setContent} />
      </div>
    </div>
  );
}
